import { CalendarDays, Clock, Search } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { PermissionGate } from '@/auth/PermissionGate';
import { BotonBiColor } from '@/components/BloquesConsalud';
import { useStagger } from '@/lib/motion';

import { Cargando, ErrorCarga, TabsModulo } from '../curso/componentes';

import { useTemario } from './queries';

const sinAcentos = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/** Temario completo (port de `curso-enarm/temario`): filtros por especialidad y búsqueda. */
export function TemarioPage() {
  return (
    <PermissionGate permiso="study_plan.access" modulo="Temario">
      <Temario />
    </PermissionGate>
  );
}

function Temario() {
  const { data, error, isPending } = useTemario();
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState('');
  const esp = params.get('especialidad');
  const sub = params.get('sub');
  const root = useRef<HTMLDivElement>(null);

  const especialidades = useMemo(() => [...new Set((data ?? []).map((d) => d.specialty))], [data]);
  const subs = useMemo(
    () => [...new Set((data ?? []).filter((d) => d.specialty === esp).map((d) => d.subspecialty))],
    [data, esp],
  );
  const filas = useMemo(() => {
    const q = sinAcentos(busca.trim());
    return (data ?? []).filter(
      (d) =>
        (!esp || d.specialty === esp) &&
        (!sub || d.subspecialty === sub) &&
        (q.length < 3 || sinAcentos(`${d.module} ${d.className}`).includes(q)),
    );
  }, [data, esp, sub, busca]);
  useStagger(root, '[data-fila]', { step: 20, y: 8, deps: [esp, sub, data] });

  return (
    <div className="space-y-6">
      <TabsModulo />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="titulo-seccion">
          <span className="subrayado-verde">Temario</span>
        </h1>
        {/* Accesos del legacy como botones bicolor (bloque marino con ícono + degradado). */}
        <div className="grid w-full gap-3 sm:w-auto sm:grid-cols-2">
          <BotonBiColor
            to="/curso-enarm/temario/organizar"
            icono={<Clock size={26} />}
            className="sm:w-[260px]"
          >
            <span className="text-sm leading-tight">¿Cómo organizo mi estudio?</span>
          </BotonBiColor>
          <BotonBiColor
            to="/curso-enarm/temario/plan"
            icono={<CalendarDays size={26} />}
            variante="verde"
            className="sm:w-[260px]"
          >
            <span className="text-sm leading-tight">Mi plan de estudio</span>
          </BotonBiColor>
        </div>
      </div>

      {/* Barra de filtros (navbar del legacy): especialidades + buscador. */}
      <div className="panel-vidrio flex flex-wrap items-center gap-2 py-3">
        <button
          type="button"
          className="btn-modulo"
          data-state={!esp ? 'active' : undefined}
          onClick={() => setParams({}, { replace: true })}
        >
          Todo
        </button>
        {especialidades.map((e) => (
          <button
            key={e}
            type="button"
            className="btn-modulo"
            data-state={esp === e ? 'active' : undefined}
            onClick={() => setParams({ especialidad: e }, { replace: true })}
          >
            {e}
          </button>
        ))}
        {esp && subs.length > 1 && (
          <select
            className="input-vidrio"
            value={sub ?? ''}
            aria-label="Subespecialidad"
            onChange={(e) =>
              setParams(
                e.target.value ? { especialidad: esp, sub: e.target.value } : { especialidad: esp },
                { replace: true },
              )
            }
          >
            <option value="">Todas las subespecialidades</option>
            {subs.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}
        <label className="relative ml-auto w-full sm:w-72">
          <Search size={16} className="absolute top-1/2 left-3 -translate-y-1/2 text-texto-3" />
          <input
            className="input-vidrio w-full pl-9"
            placeholder="Buscar un tema (mínimo 3 letras)"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </label>
      </div>

      {isPending && <Cargando />}
      {error && <ErrorCarga error={error} />}
      {data && (
        <div ref={root} className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs tabla-cs-verde">
            <thead>
              <tr>
                <th className="text-center">#</th>
                <th>Módulo</th>
                <th>Especialidad</th>
                <th>Sub-especialidad</th>
                <th>Clase</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={`${String(f.number)}-${String(f.classId)}`} data-fila>
                  <td className="text-center tabular">{f.number}</td>
                  <td className="font-bold">{f.module}</td>
                  <td>{f.specialty}</td>
                  <td>{f.subspecialty}</td>
                  <td>
                    <Link
                      to={`/curso-enarm/clases/${String(f.classId)}`}
                      className="font-bold whitespace-nowrap text-azul-texto underline"
                    >
                      Ver clase
                    </Link>
                  </td>
                </tr>
              ))}
              {filas.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-texto-2">
                    No encontramos temas con ese filtro.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
